// R8 — **THE DAILY CARD MODAL, AND THE FIVE-STATE LAW NOTHING COULD RUN.** (Extracted by TRAP-1a,
// which needed the ceiling paid and took it in this ledger's currency: the thing that had to move is
// the thing nothing could reach.)
//
// >>> R75's OWN FINDING LIVES IN HERE AND WAS HELD BY NOTHING. <<< That round found the read had NO
// status check at all, so **a 401, a 500 and an unreachable database all landed on `empty`** -- and
// in this modal `empty` is an INVITATION TO TAP THE BUTTON THAT SPENDS. Only a route that really
// answered `card: null` is *you have not drawn today*; everything else is an error. Five states
// rather than two (R18), and inside an eleven-thousand-line file no `.mjs` gate could load one of
// them.
//
// And OV2-4's ruling rides with it: only a card drawn in THIS sitting arrives face down. Re-opening
// today's card later shows it face up, because she already turned it -- the ritual is the draw, not
// the modal.
//
// It holds hooks, so it is the `TierCards` / `MicButton` class rather than a hookless leaf a gate
// CALLS: **the reach it buys is the IMPORT.** Moved VERBATIM -- the tree it renders is byte-identical,
// so this carries no render risk.
import { useEffect, useState } from "react";
import { ModalBackdrop } from "./ModalBackdrop";
import { readDailyCard, drawDailyCard, type DailyCard } from "../lib/daily-room";
import { TarotFlipCard, CardZoomOverlay } from "./TarotViews";
import { AskLalaAboutButton } from "./AskLalaAboutButton";
import { stripMarkdown } from "../lib/format";

export function DailyCardModal({ onClose }: { onClose: () => void }) {
  const [card, setCard] = useState<DailyCard | null>(null);
  const [status, setStatus] = useState<"loading" | "empty" | "drawing" | "ready" | "error">("loading");
  const [zoomed, setZoomed] = useState(false);
  // OV2-4: only a card drawn in THIS sitting arrives face down. Re-opening today's card later shows it
  // face up, because she already turned it — the ritual is the draw, not the modal.
  const [freshDraw, setFreshDraw] = useState(false);
  const [turnedToday, setTurnedToday] = useState(false);

  // R75: the read had NO status check at all, so a 401, a 500 and an unreachable database all landed
  // on "empty" — which in this modal is an invitation to tap the button that spends. Only a route
  // that really answered `card: null` is "you have not drawn today"; everything else is an error.
  useEffect(() => {
    let active = true;
    void readDailyCard().then((a) => {
      if (!active) return;
      if (a.status === "ok") { setCard(a.value); setStatus("ready"); }
      else setStatus(a.status === "none" ? "empty" : "error");
    });
    return () => { active = false; };
  }, []);

  const draw = async () => {
    setStatus("drawing");
    const a = await drawDailyCard();
    if (a.status === "ok") { setCard(a.value); setFreshDraw(true); setTurnedToday(false); setStatus("ready"); }
    else setStatus("error");
  };

  return (
    // M4-2: the X and the heading sit OUTSIDE the scrolling area. `.daily-card` used to scroll as a
    // whole with the X positioned absolutely inside it, so the moment a member scrolled down to read the
    // interpretation the X went off the top of the screen (measured: top: -101px). With no X in sight,
    // Back was the only apparent way out, and Back leaves My Room entirely. That is the bug.
    <ModalBackdrop label="Your daily card" className="daily-card-backdrop" onClose={onClose}>
      <div className="daily-card">
        <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        <p className="eyebrow">Today</p>
        <h2>Your Daily Card</h2>
        <div className="daily-card__body">
        {status === "loading" ? (
          <p className="daily-card__note">Opening the cards…</p>
        ) : status === "empty" || status === "drawing" ? (
          <>
            <p className="daily-card__note">
              {status === "drawing" ? "Turning your card…" : "One card for the energy of your day. Draw when you are ready."}
            </p>
            <button
              className={status === "drawing" ? "button button--primary is-generating" : "button button--primary"}
              type="button"
              onClick={draw}
              disabled={status === "drawing"}
            >
              {status === "drawing" ? "Drawing…" : "Draw my card"}
            </button>
          </>
        ) : status === "ready" && card ? (
          <div className="daily-card__reveal">
            {/* OV2-4: a card she has JUST drawn comes out face down and she turns it herself. Today's
                card opened again later is already face up — she turned it this morning. */}
            <div className="daily-card__imgbtn">
              {/* N4-1: this one drew the art upright while the caption underneath said "(reversed)". */}
              <TarotFlipCard
                slug={card.slug}
                name={card.name}
                orientation={card.orientation}
                imgClassName="daily-card__image"
                faceDown={freshDraw && !turnedToday}
                onTurn={() => setTurnedToday(true)}
                onZoom={() => setZoomed(true)}
              />
            </div>
            {freshDraw && !turnedToday ? (
              <h3 className="daily-card__name daily-card__name--hidden">Tap to turn it over</h3>
            ) : (
              <h3 className="daily-card__name">
                {card.name}{card.orientation === "reversed" ? " (reversed)" : ""}
              </h3>
            )}
            {/* OV2-4: the words are UNCHANGED, but they wait for the turn — a paragraph naming the card
                under a face-down card would hand her the answer before she touched it. */}
            {card.interpretation && !(freshDraw && !turnedToday) ? <p className="daily-card__interp">{stripMarkdown(card.interpretation)}</p> : null}
            {/* N16-3 ROOT CAUSE: this surface renders the RAW draw from /api/tarot/daily, which has no
                `type`, so it was never a "report" and the shared button's allowlist could never see it.
                The card is shaped into a reading here, so the one shared component covers it too. */}
            <div className="daily-card__actions">
              <AskLalaAboutButton
                report={{
                  type: "tarot",
                  kicker: "Your daily card",
                  title: card.name,
                  cards: [{ name: card.name, position: "Today", orientation: card.orientation, interpretation: card.interpretation }],
                  overview: card.overview,
                }}
                kind="reading"
              />
            </div>
          </div>
        ) : (
          <p className="daily-card__note">The card could not be drawn right now. Please try again.</p>
        )}
        </div>
      </div>
      {zoomed && card ? (
        <CardZoomOverlay src={`/api/assets/tarot/${card.slug}`} alt={card.name} reversed={card.orientation === "reversed"} onClose={() => setZoomed(false)} />
      ) : null}
    </ModalBackdrop>
  );
}

